export const months: Record<number, string> = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December',
}

export const usStates: Record<string, string> = {
  'AL': 'Alabama',
  'AK': 'Alaska',
  'AZ': 'Arizona',
  'AR': 'Arkansas',
  'CA': 'California',
  'CO': 'Colorado',
  'CT': 'Connecticut',
  'DE': 'Delaware',
  'DC': 'District Of Columbia',
  'FL': 'Florida',
  'GA': 'Georgia',
  'HI': 'Hawaii',
  'ID': 'Idaho',
  'IL': 'Illinois',
  'IN': 'Indiana',
  'IA': 'Iowa',
  'KS': 'Kansas',
  'KY': 'Kentucky',
  'LA': 'Louisiana',
  'ME': 'Maine',
  'MD': 'Maryland',
  'MA': 'Massachusetts',
  'MI': 'Michigan',
  'MN': 'Minnesota',
  'MS': 'Mississippi',
  'MO': 'Missouri',
  'MT': 'Montana',
  'NE': 'Nebraska',
  'NV': 'Nevada',
  'NH': 'New Hampshire',
  'NJ': 'New Jersey',
  'NM': 'New Mexico',
  'NY': 'New York',
  'NC': 'North Carolina',
  'ND': 'North Dakota',
  'OH': 'Ohio',
  'OK': 'Oklahoma',
  'OR': 'Oregon',
  'PA': 'Pennsylvania',
  'RI': 'Rhode Island',
  'SC': 'South Carolina',
  'SD': 'South Dakota',
  'TN': 'Tennessee',
  'TX': 'Texas',
  'UT': 'Utah',
  'VT': 'Vermont',
  'VA': 'Virginia',
  'WA': 'Washington',
  'WV': 'West Virginia',
  'WI': 'Wisconsin',
  'WY': 'Wyoming'
}

export const caProvinces: Record<string, string> = {
  'NL': 'Newfoundland and Labrador',
  'PE': 'Prince Edward Island',
  'NS': 'Nova Scotia',
  'NB': 'New Brunswick',
  'QC': 'Quebec',
  'ON': 'Ontario',
  'MB': 'Manitoba',
  'SK': 'Saskatchewan',
  'AB': 'Alberta',
  'BC': 'British Columbia',
  'YT': 'Yukon',
  'NT': 'Northwest Territories',
  'NU': 'Nunavut',
}

export const countries: Record<string, Record<string, string>> = {
  'United States': usStates,
  'Canada': caProvinces,
}

/**
 * GICS L3 industry classifications (74), as `code: "Industry (Sector)"`.
 *
 * Shared rather than owned by one app: IAM collects this during onboarding and
 * the back office has to be able to correct it on the legacy members that
 * predate the field, and a second copy of a taxonomy is a second thing to keep
 * in step. Mirrors Entity::INDUSTRY_CLASSIFICATIONS in IAM and
 * sustain/db/fixtures/industries.yml, where the sector drives benchmarking.
 */
export const industryClassifications: Record<string, string> = {
  // Energy
  '101010': 'Energy Equipment & Services (Energy)',
  '101020': 'Oil, Gas & Consumable Fuels (Energy)',
  // Basic Materials
  '151010': 'Chemicals (Basic Materials)',
  '151020': 'Construction Materials (Basic Materials)',
  '151030': 'Containers & Packaging (Basic Materials)',
  '151040': 'Metals & Mining (Basic Materials)',
  '151050': 'Paper & Forest Products (Basic Materials)',
  // Industrials
  '201010': 'Aerospace & Defense (Industrials)',
  '203010': 'Air Freight & Logistics (Industrials)',
  '201020': 'Building Products (Industrials)',
  '202010': 'Commercial Services & Supplies (Industrials)',
  '201030': 'Construction & Engineering (Industrials)',
  '201040': 'Electrical Equipment (Industrials)',
  '203040': 'Ground Transportation (Industrials)',
  '201050': 'Industrial Conglomerates (Industrials)',
  '201060': 'Machinery (Industrials)',
  '203030': 'Marine Transportation (Industrials)',
  '203020': 'Passenger Airlines (Industrials)',
  '202020': 'Professional Services (Industrials)',
  '201070': 'Trading Companies & Distributors (Industrials)',
  '203050': 'Transportation Infrastructure (Industrials)',
  // Consumer Cyclicals
  '251010': 'Automobile Components (Consumer Cyclicals)',
  '251020': 'Automobiles (Consumer Cyclicals)',
  '255010': 'Broadline Retail (Consumer Cyclicals)',
  '253020': 'Diversified Consumer Services (Consumer Cyclicals)',
  '254010': 'Distributors (Consumer Cyclicals)',
  '252010': 'Household Durables (Consumer Cyclicals)',
  '253010': 'Hotels, Restaurants & Leisure (Consumer Cyclicals)',
  '252020': 'Leisure Products (Consumer Cyclicals)',
  '255020': 'Specialty Retail (Consumer Cyclicals)',
  '252030': 'Textiles, Apparel & Luxury Goods (Consumer Cyclicals)',
  // Consumer Non-Cyclicals
  '302010': 'Beverages (Consumer Non-Cyclicals)',
  '301010': 'Consumer Staples Distribution & Retail (Consumer Non-Cyclicals)',
  '302020': 'Food Products (Consumer Non-Cyclicals)',
  '303010': 'Household Products (Consumer Non-Cyclicals)',
  '303020': 'Personal Care Products (Consumer Non-Cyclicals)',
  '302030': 'Tobacco (Consumer Non-Cyclicals)',
  // Healthcare
  '352010': 'Biotechnology (Healthcare)',
  '351010': 'Health Care Equipment & Supplies (Healthcare)',
  '351020': 'Health Care Providers & Services (Healthcare)',
  '351030': 'Health Care Technology (Healthcare)',
  '352030': 'Life Sciences Tools & Services (Healthcare)',
  '352020': 'Pharmaceuticals (Healthcare)',
  // Financials
  '401010': 'Banks (Financials)',
  '402030': 'Capital Markets (Financials)',
  '402020': 'Consumer Finance (Financials)',
  '402010': 'Financial Services (Financials)',
  '403010': 'Insurance (Financials)',
  '402040': 'Mortgage Real Estate Investment Trusts (Financials)',
  // Technology
  '452010': 'Communications Equipment (Technology)',
  '501010': 'Diversified Telecommunication Services (Technology)',
  '452030': 'Electronic Equipment, Instruments & Components (Technology)',
  '502020': 'Entertainment (Technology)',
  '502030': 'Interactive Media & Services (Technology)',
  '451020': 'IT Services (Technology)',
  '502010': 'Media (Technology)',
  '453010': 'Semiconductors & Semiconductor Equipment (Technology)',
  '451030': 'Software (Technology)',
  '452020': 'Technology Hardware, Storage & Peripherals (Technology)',
  '501020': 'Wireless Telecommunication Services (Technology)',
  // Utilities
  '551010': 'Electric Utilities (Utilities)',
  '551020': 'Gas Utilities (Utilities)',
  '551050': 'Independent Power & Renewable Electricity Producers (Utilities)',
  '551030': 'Multi-Utilities (Utilities)',
  '551040': 'Water Utilities (Utilities)',
  // Real Estate
  '601010': 'Diversified REITs (Real Estate)',
  '601040': 'Health Care REITs (Real Estate)',
  '601025': 'Hotel & Resort REITs (Real Estate)',
  '601020': 'Industrial REITs (Real Estate)',
  '601030': 'Office REITs (Real Estate)',
  '602010': 'Real Estate Management & Development (Real Estate)',
  '601050': 'Residential REITs (Real Estate)',
  '601060': 'Retail REITs (Real Estate)',
  '601070': 'Specialized REITs (Real Estate)',
}

/**
 * Legal forms a member can be. Validated by inclusion in IAM, so the list the
 * back office offers has to be the same list, not a copy of it.
 */
export const formsOfOrganization = [
  'C-Corporation',
  'S-Corporation',
  'Corporation (Unspecified)',
  'Limited Liability Company',
  'Limited Partnership',
  'General Partnership',
  'Sole Proprietor',
  'Trust/Estate',
  'Other',
]

/**
 * How a member states its size. Validated by inclusion in IAM, same as the
 * forms of organization above.
 */
export const organizationSizeTypes = [
  'Assets',
  'Annual Revenue'
]
